import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { RoleSet, RoleSets } from '../../../../roleAssignments/data'
import { SupplierList } from '../../../../suppliers/data'
import { FormFieldName } from './EditAssignee.container'

type Props = {
  formik: FormikProps<any>
  isFormFieldInvalid: (name: FormFieldName) => boolean
  getFormErrorMessage: (name: FormFieldName) => JSX.Element
  config: any
  roleSets?: RoleSets
  suppliers?: SupplierList
  projectRoles?: (string | undefined)[]
  visible: boolean
  onHide: () => void
}

export const EditAssignee: React.FC<Props> = ({
  formik,
  isFormFieldInvalid,
  getFormErrorMessage,
  config,
  roleSets,
  suppliers,
  projectRoles,
  visible,
  onHide,
}) => {
  const { t } = useTranslation()

  const complete = () => {
    formik.submitForm()
  }

  // Dialog components
  // Assignee
  const assigneeLabel = (
    <label
      htmlFor="assignee"
      className={classNames('text-sm', {
        'p-error': isFormFieldInvalid('assignee'),
      })}
    ></label>
  )

  const assigneeComponent = (
    <InputText
      id="assignee"
      className={classNames('w-full', {
        'p-invalid': isFormFieldInvalid('assignee'),
      })}
      value={formik.values.assignee}
      onChange={formik.handleChange}
    />
  )

  const assigneeErrorMsg = <div>{getFormErrorMessage('assignee')}</div>

  const assigneeWrapper = (
    <ConfigurableComponent
      config={config}
      component={assigneeComponent}
      label={assigneeLabel}
      errorMsg={assigneeErrorMsg}
    />
  )

  // Role
  const roleLabel = (
    <label
      htmlFor="role"
      className={classNames('text-sm', {
        'p-error': isFormFieldInvalid('role'),
      })}
    ></label>
  )

  const roleValueTemplate = (option: RoleSet, props: any) => {
    if (option) {
      return <span>{option.role.name}</span>
    }
    return <span>{props.placeholder}</span>
  }

  const roleItemTemplate = (option: RoleSet) => {
    return (
      <>
        <div className="flex align-items-center">
          <div className="flex flex-column my-1 mr-3">
            <div className="font-semibold">{option.role.name}</div>
          </div>
        </div>
      </>
    )
  }

  const roleComponent = (
    <Dropdown
      id="role"
      className={classNames('w-full', {
        'p-invalid': isFormFieldInvalid('role'),
      })}
      value={formik.values.role}
      onChange={formik.handleChange}
      options={roleSets}
      valueTemplate={roleValueTemplate}
      itemTemplate={roleItemTemplate}
      optionValue="role.name"
      optionLabel="role.name"
      filter
      filterBy="role.name"
    />
  )

  const roleErrorMsg = <div>{getFormErrorMessage('role')}</div>

  const roleWrapper = (
    <ConfigurableComponent
      config={config}
      component={roleComponent}
      label={roleLabel}
      errorMsg={roleErrorMsg}
    />
  )

  // Permission
  const selectedRole = formik.values.role
  const matchingRoleSet = roleSets?.find(
    roleSet => roleSet.role.name === selectedRole,
  )
  let descriptions: string[] | undefined
  if (matchingRoleSet) {
    descriptions = matchingRoleSet.permissions.map(
      permission => permission.description,
    )
  }

  const permissions = (
    <>
      <h5 className="my-0">
        {t('members.table.edit.dialog.form.permissions.label')}
      </h5>
      <ul className="mt-0 pl-3 h-7rem overflow-y-auto">
        {descriptions?.map(description => (
          <li key={description} className="text-sm">
            {description}
          </li>
        ))}
      </ul>
    </>
  )

  // Header
  const header = <>{t('members.table.edit.dialog.title')}</>

  // Footer
  const footer = (
    <>
      <Button
        label={t('members.table.edit.dialog.cancel.label')}
        icon="pi pi-times"
        className="p-button-text mt-3 w-full md:w-auto mb-1 md:mb-0"
        onClick={() => onHide()}
      />
      <Button
        label={t('members.table.edit.dialog.save.label')}
        icon={'pi pi-check'}
        className="w-full md:w-auto"
        onClick={complete}
        disabled={!formik.dirty}
      />
    </>
  )

  return (
    <>
      <Dialog
        visible={visible}
        header={header}
        style={{ width: '30rem' }}
        breakpoints={{ [calculateBreakpointWidth(30)]: '90vw' }}
        footer={footer}
        onHide={() => onHide()}
        blockScroll
      >
        <div className="formgrid grid">
          <div className="col-12">{assigneeWrapper}</div>

          <div className="col-12">
            {roleWrapper}
            {descriptions ? permissions : ''}
          </div>
        </div>
      </Dialog>
    </>
  )
}
